package dev.kotlinfest._2026.models

import kotlinx.browser.localStorage

object MyTimetable {
    private const val StorageKey = "kotlinfest-2026:my-timetable-session-ids"
    private const val GuideStorageKey = "kotlinfest-2026:my-timetable-guide-seen"
    private const val NotificationsStorageKey = "kotlinfest-2026:session-notifications-enabled"

    fun read(): Set<String> = localStorage.getItem(StorageKey)
        ?.split(',')
        ?.filter { UserSessions.find(it) != null }
        ?.toSet()
        .orEmpty()

    fun write(sessionIds: Set<String>) {
        localStorage.setItem(
            StorageKey,
            UserSessions.all.map { it.id }.filter { it in sessionIds }.joinToString(","),
        )
    }

    fun encode(sessionIds: Set<String>): String = UserSessions.all.map { it.id }.filter { it in sessionIds }.joinToString(".")

    fun decode(value: String?): Set<String> = value
        ?.split('.')
        ?.filter { UserSessions.find(it) != null }
        ?.toSet()
        .orEmpty()

    fun hasSeenGuide(): Boolean = localStorage.getItem(GuideStorageKey) == "true"

    fun markGuideAsSeen() {
        localStorage.setItem(GuideStorageKey, "true")
    }

    fun notificationsEnabled(): Boolean = localStorage.getItem(NotificationsStorageKey) == "true"

    fun setNotificationsEnabled(enabled: Boolean) {
        localStorage.setItem(NotificationsStorageKey, enabled.toString())
    }
}

data class UserSession(
    val id: String,
    val time: String,
    val title: String,
    val speaker: String,
    val affiliation: String?,
    val account: String,
    val avatarPath: String,
    val venue: String,
    val category: String,
    val level: String?,
    val description: List<String>,
    val targetAudience: String?,
)

private data class ProposalDetails(
    val account: String,
    val category: String,
    val level: String,
    val description: List<String>,
)

private val proposalDetailsBySessionId = mapOf<String, ProposalDetails>(
    "session-02" to ProposalDetails(
        account = "@kroyeeg",
        category = "テスト",
        level = "中級者",
        description = listOf(
            "AIコーディングで、実装を書く速度だけでなく既存実装を作り替える速度も上がりました。内部構造に依存したテストは、実装を何度も試す開発では変更の足かせになりがちです。",
            "本セッションでは、ユニットテストを否定せず、AI時代に何を固定すべきかを見直します。HTTP / GraphQL APIの境界を動かし、「外から見た振る舞い」を固定するAPIレベルのテストをKotestでどう書くかを、次の観点で扱います。",
            "• DescribeSpecによるHTTP / GraphQL APIテストの構成",
            "• Data Driven Testingで業務ルールや境界値を並べる",
            "• TestcontainersでDB込みに境界を動かす",
            "• 検証をassertionの羅列でなく仕様の言葉に近づける",
            "• 認証済みclientやfixture、assertion helperをSpec継承に閉じ込めず再利用する",
        ),
    ),
    "session-03" to ProposalDetails(
        account = "@mntsh",
        category = "Kotlin言語・プログラミング",
        level = "中級者",
        description = listOf(
            "拡張関数 Foo.show() と Bar.show() があるとしましょう。このとき、Foo と Bar をまとめて fun showTwice(x: ???) { x.show(); x.show() } のように取り扱えたら便利だと思いませんか？ それ、実現できます。そう、context parameters ならね。",
            "Context parameters は Kotlin 2.4.0 で安定版として利用可能になり、特に依存関係の注入やスコープの制限を実現する手段として注目を集めています。しかし、それ以外にも「既存のコードに手を加えることなく、異なるクラスをまとめて取り扱う」という強力な使い方もできるのです。",
            "本発表では、その根底にある「型クラス」という概念から丁寧に解説し、context parameters を使った新しい書き方を紹介します。",
        ),
    ),
    "session-04" to ProposalDetails(
        account = "@_naru_naru_0",
        category = "Webバックエンド／サーバサイド",
        level = "中級者",
        description = listOf(
            "複雑な検索条件を受け取る検索APIは、GETでは表現しきれず、POSTでは意味論が崩れます。この悩みに応えるHTTP QUERYが、今年RFC 10008として標準化されました。ただしRFCが決めるのは意味論までで、クエリの形は設計者に委ねられます。",
            "Bill Oneでは、この委ねられた部分をKotlinの型システムで設計しました。取得フィールド・検索条件・並び順を指定できるリクエストボディを、不正な指定がそもそも書けない形で定義しています。 以下を実コードを交えつつ紹介します。",
            "・検索条件の表現方式をどう選定したか",
            "・data class・enum・sealed interfaceで「不正な条件を書けなくする」設計",
            "・KtorのQUERY対応の現在地と、Google Cloudで直面した現実",
            "検索APIに悩むKotlinエンジニアに、設計判断の一助となる話をします。",
        ),
    ),
    "session-05" to ProposalDetails(
        account = "@matthew3880",
        category = "Kotlin Multiplatform／Compose Multiplatform",
        level = "初心者",
        description = listOf(
            "Roomは2025年4月の2.7.0でKotlin Multiplatform対応の安定版となりました。",
            "技術選定当時、iOSも含めたマルチプラットフォームでの実践知がまだ少ない中、PoC検証を経てRoomの採用を決断しました。",
            "大規模アルバイト求人アプリでは、元々iOSとAndroidで異なるデータベースライブラリで運用されており、Kotlin Multiplatformでの全面リニューアルを機に、ローカルデータベースをRoomで統一しました。",
            "本セッションでは、iOSからRoomを利用する際の勘所、マスタデータのプリセット同梱、旧アプリからのデータ移行、暗号化対応など、統一の過程での現場の取り組みや課題を、実例とともにお話しします。",
            "Kotlin MultiplatformやRoomの採用を検討されている方に、判断材料と実践の勘所を持ち帰っていただければ幸いです。",
        ),
    ),
    "session-06" to ProposalDetails(
        account = "@takemioIO",
        category = "インフラ",
        level = "中級者",
        description = listOf(
            "KotlinのWebアプリのオブザーバビリティにはJava agentや商用APMがよく使われますが、本講演では別の入口として、ホスト側から通信やプロセスを観測するeBPF計装を扱います。",
            "コード、依存関係、ビルド成果物を変えずに、KotlinのWebアプリをどこまでトレースできるかをKtorで検証します。kprobeでレスポンスタイムとステータスコードを取り、USDTでメソッド実行を観測し、OBIで分散トレースを取得します。",
            "Kotlin coroutineでは、suspend関数の実行がスレッドをまたぐため、スレッドを手がかりにした文脈追跡だけでは、リクエストと後続処理の対応づけが難しくなります。",
            "そこで、Continuationとコルーチンのディスパッチを手がかりに、受信リクエストから外部呼び出しまでのトレースをつなぐ方法を検証します。",
        ),
    ),
    "session-07" to ProposalDetails(
        account = "@kitakkun_pb",
        category = "Kotlin言語・プログラミング",
        level = "中級者",
        description = listOf(
            "AIがコーディングを担う時代、コーディング規約は自然言語で書かれた曖昧な約束ではなく、コンパイルエラーに落ちる厳密な制約として設計するべきです。",
            "コードの一貫性と品質を保つ基盤として広く使われるLintは、構文木やコンパイラの解析結果を利用するラッパーAPIの上に成り立っており、言語の進化に追従した完全な解析は容易ではありません。",
            "FIR Checkerは、K2コンパイラ拡張の一つで、コンパイラが解決した完全な意味情報の上でコードを検査できます。",
            "本セッションでは、この仕組みを使って「間違ったコード・書いてほしくないコード」をコンパイル不能にする、いわばガードレールの作り方を紹介します。",
            "Checkerの種類と規約ごとの選び方から、制御フローを辿る一歩踏み込んだ検査、宣言にメタデータを付与しモジュール境界を越えて働くCheckerを作るテクニックまで、実装と運用の勘所をお話しします。",
        ),
    ),
    "session-08" to ProposalDetails(
        account = "@shiita_0903",
        category = "Kotlin言語・プログラミング",
        level = "中級者",
        description = listOf(
            "Kotlinでエラーを扱う方法には、例外、kotlin.Result、sealed classによる独自Result、ArrowのEither/Raise、そして現在KEEPで提案中のRich Errorsまで、多くの選択肢があります。",
            "本セッションでは「関数が失敗する可能性を、コードのどこに表すか」という軸でこれらの手法を整理し、それぞれが何を解決し、どんな課題を残したのかを順に見ていきます。",
            "各手法の特徴と限界を知ることで、自分のプロジェクトに合った手法を選ぶ手がかりになるはずです。",
        ),
    ),
    "session-09" to ProposalDetails(
        account = "@rioX432",
        category = "ミドルウェア／フレームワーク",
        level = "上級者",
        description = listOf(
            "KotlinアプリにUnityを組み込むと、KotlinとC#の境界が型安全性と性能のボトルネックになります。Avvyでは、手書きブリッジの型ずれによる実行時クラッシュをなくすため、C#のインターフェースからKotlin／Swiftのコードを生成し、コールバック型のAPIをKotlinのsuspend関数として扱える仕組みを構築しました。さらに、高頻度のフェイストラッキングデータを文字列メッセージから共有メモリへ移し、自社計測では転送処理を約27倍高速化しました。本セッションでは、型マッピング、非同期API、生成コードの責務、共有メモリのレイアウトと互換性まで、異なるランタイムをつなぐ設計を解説します。",
        ),
    ),
    "session-10" to ProposalDetails(
        account = "@chigichan24",
        category = "Kotlin Multiplatform／Compose Multiplatform",
        level = "中級者",
        description = listOf(
            "KMPは今やAndroid・iOS間でコードベースを共有しながら、十分なクオリティのアプリを作れるほど機能が充実し、プロダクション導入する事例を多く見聞きするようになりました。",
            "私たちのアプリもAndroid・iOSを別コードベースで開発しており、開発効率向上を求めてKMP導入を検証しました。Swift Exportを中心に目まぐるしく追加される機能や、他社での運用事例を検証したうえで、「現時点では」KMP導入を見送るという結論に至りました。同時に「どうなったら再開するか」も検討し、Swift Exportの進化に求められる条件や、今のAndroid・iOSコードベース側で解消すべき技術的負債を具体的に言語化しています。",
            "このセッションでは、Swift Exportの技術的な現在地にも触れながら、この一連の検討プロセスを皆さんのチームでもそのまま使える形で共有します。",
        ),
    ),
    "session-11" to ProposalDetails(
        account = "@yyh_gl",
        category = "テスト",
        level = "中級者",
        description = listOf(
            "「たまに落ちるテスト」や「本番環境でのみ起きるレースコンディション」など、並行処理の不具合は再現が難しく、しばしば私たちの手を煩わせます。",
            "「rerunしたら通った」で済ませた経験がある人も多いのではないでしょうか。",
            "JetBrains製のテストフレームワークLincheckは、この問題に対し、系統的に探索するモデル検査と、実行順序を大量に試すストレステストという2つのアプローチで解決を図ります。",
            "本発表では、Lincheckの使い方を通じて次の考え方を紹介します。",
            "・並行処理の「正しさ」をどう定義するか",
            "・「たまに落ちるテスト」を祈りに任せず制御下に置くには",
            "・suspend関数を含む並行処理をどう検証するか",
            "・テストを宣言的に書くには",
            "Lincheckというツールの知識だけでなく、並行処理という厄介な問題との向き合い方を持ち帰っていただくことが本発表のゴールです。",
        ),
    ),
    "session-12" to ProposalDetails(
        account = "@cmd8051020498",
        category = "Kotlinの活用事例(仕事や学業など)",
        level = "初心者",
        description = listOf(
            "上位AIモデルの登場が続く一方、従量課金化と利用量制限で最強モデルに使い放題で書かせる前提は（学生には）崩れつつあります。",
            "学生の私は、卒業研究のドローン映像共有システム(DJI SDKのAndroidアプリ+Ktorサーバ、フルKotlin)を、人間1人+AIエージェント体制で開発しています。",
            "選んだ戦略は、重課金せず、下位モデルでも実装をやり切れる状態まで仕様に投資すること。",
            "このセッションでは、Kotlin初心者だった学生が実システムを完成させるまでの進め方を、実物の設計資料と失敗談つきで紹介します。",
            "上位モデルが出るたびに焦らなくていい、明日から真似できる始め方を持ち帰ってください。",
        ),
    ),
    "session-13" to ProposalDetails(
        account = "@ev_yug",
        category = "Webバックエンド／サーバサイド",
        level = "初心者",
        description = listOf(
            "長らくPythonでサービスを提供してきた弊社ですが、この度共通基盤を実装するにあたってKotlinとQuarksを採用しました。",
            "以下について、理由と振り返りを交えながら紹介したいと思います。",
            "• 数ある言語の中からどうしてKotlinを選んだのか",
            "• 数あるフレームワークの中からどうしてQuarksを選んだのか",
            "• そしてどんなソフトウェア・アプリケーションアーキテクチャで実装したのか",
        ),
    ),
    "session-14" to ProposalDetails(
        account = "@ternbusty",
        category = "Kotlin言語・プログラミング",
        level = "上級者",
        description = listOf(
            "筆者は、 Google Summer of Code で選出された Contributor として Kotlin/Wasm に末尾呼び出し最適化を実装し、再帰を多用するコードで Stack Overflow が起こらないようにする貢献に取り組みました。この経験をもとに、本トークでは、コンパイラの内部実装に踏み込んだ問題解決の方法、および Kotlin エコシステムへの貢献の楽しさを共有します。",
            "このトークで得られるもの",
            "• Kotlin コンパイラコードの歩き方",
            "• 実体験に基づいた、Kotlin コンパイラへの貢献の流れ",
            "対象者",
            "• Kotlin エコシステムへの contribute に興味があるが、一歩を踏み出せていない人",
            "• Kotlin コンパイラ実装に deep dive したい人",
            "• 末尾呼び出しをはじめとした、コンパイラによる最適化の仕組みに興味のある人",
        ),
    ),
    "session-15" to ProposalDetails(
        account = "@HalunoYo",
        category = "ツール／エコシステム",
        level = "上級者",
        description = listOf(
            "Android開発などKotlinプロジェクトのCIやテスト実行時間の肥大化は深刻な課題です。",
            "解決概念\"Affected Test Selection\"も多くはファイル/モジュール単位に留まり、関数単位の絞り込みは困難でした。",
            "本セッションでは、UnstableなKotlin Analysis API Standaloneとグラフ理論(BFS, Topological Sortなど)を組み合わせ、関数レベルのAffected Test Selectionを実現する自作OSSの設計と実装を解説します。",
            "安定版情報がほぼ無い領域をOSS化するまでの過程をAPIやアルゴリズムの視点からお話します。",
            "また、併せて現在の課題も正直に共有します。",
            "※本OSSはアルファ段階のため、技術的な設計と実装プロセスに焦点を当てます。",
        ),
    ),
    "session-16" to ProposalDetails(
        account = "@getupmax",
        category = "ツール／エコシステム",
        level = "初心者",
        description = listOf(
            "KotlinConf 2026で、ktfmtというKotlinフォーマッターを標準化し、Kotlinの中核に据える作業が始まっていることが紹介されました。一方で、Kotlinのフォーマッター・リンターは以前としてDetektやKtlintが主流であり、ktfmtはまだ広く使われていないと感じています。本セッションでは、私がDetektからktfmtに移行した経験を元に、ktfmtの思想やDetektとの違い、移行して良かった点などを紹介します。本セッションで、来るべきktfmtの標準化に備え、今からスムーズにktfmtを導入・活用するための実践的な知識を先取りしましょう！",
        ),
    ),
    "session-17" to ProposalDetails(
        account = "@AkiraIto27",
        category = "Kotlin言語・プログラミング",
        level = "中級者",
        description = listOf(
            "Javaと同じ感覚でKDocを書いていませんか？KDocはJavadocとよく似ていますが、Markdown記法やKotlin特有のタグなど設計思想から異なります。さらに近年は、KDocがAIエージェントに読まれる対象へと役割を広げつつあります。",
            "本発表では以下の4点を扱います。",
            "• KDocとJavadocの違いと、Java出身者が無意識に持ち込みがちなアンチパターン",
            "• AndroidX公式ガイドラインに学ぶ、効果的なKDocの書き方",
            "• KDocの書き方の違いはAIエージェントの精度に影響するのか？独自検証の結果報告",
            "• 今後の展望：KDocがAIエージェントに読まれるようになる最新動向の紹介",
            "本発表を通じて、なんとなく書いていたKDocを見直し、人間にもAIにも伝わるドキュメントコメントを書けるようになることを目標とします。",
        ),
    ),
    "session-18" to ProposalDetails(
        account = "@madaozaku",
        category = "ミドルウェア／フレームワーク",
        level = "中級者",
        description = listOf(
            "車載機器と連携する「コンパニオンアプリ」開発では、Bluetooth / BLEの状態管理が開発者を悩ませ続けています。",
            "非同期に発生する接続状態の遷移、予期せぬ切断、認証フロー（Trusted Device Flow等）を絡めたハンドシェイクなど、複雑な状態管理を破綻させずに実装するのは容易ではありません。本セッションでは、Swiftエコシステムで人気の「TCA（The Composable Architecture）」に着目し、筆者が携わるAndroid Automotive OS（AAOS）とCompanion Device Connectivity（CDC）を用いた車載コンパニオンアプリPoCで得た知見をもとに、TCAの単方向データフロー（UDF）と副作用（Effect）の管理機構を活用してBLE通信を美しく、テスト可能で、堅牢なアーキテクチャに落とし込む方法を検証・解説します。",
        ),
    ),
    "session-19" to ProposalDetails(
        account = "@jsoizo",
        category = "Kotlin Multiplatform／Compose Multiplatform",
        level = "中級者",
        description = listOf(
            "kotlin-csvというライブラリについて、",
            "当初はJVM用として作られていたところにKMP対応を追加していたのですが、",
            "API設計やテストFWやツールなどがハードルとなり、KMPでの対象プラットフォームを増やす活動が難しくなっていました。",
            "そこで、メジャーバージョンアップというかたちで、APIの見直しやツールなどを生成AIを活用しつつ一気に置き換えました。",
            "しかしこのバージョンアップが極めて大掛かりな変更となってしまったため、",
            "移行用バージョンの作成やドキュメント整備、パフォーマンスリグレッションテストの実施などを",
            "丁寧に行うことでうまくリリースすることができました。",
            "このセッションでは、具体的に何が課題でどのように解決をしたのかについて説明し、",
            "KMPを前提としてライブラリはどのような設計になっていればよいか、",
            "どのようなツールを選ぶのがよいのかについての考えをお話します。",
        ),
    ),
    "session-20" to ProposalDetails(
        account = "@ngsw_taro",
        category = "Kotlin言語・プログラミング",
        level = "中級者",
        description = listOf(
            "Kotlinの関数型プログラミングライブラリであるArrow。",
            "初期のArrowは他言語の関数型パラダイムをそのままKotlinに持ち込むアプローチを取っていましたが、今では「Direct Style」というスタイルへと大きくパラダイムシフトしました。",
            "これにより、かつて「ハードルが高い」と敬遠されがちだった関数型プログラミングが、Kotlinらしい自然な構文で記述できるようになっています。",
            "本セッションでは、その「関数型っぽくない関数型のスタイル」を、Arrowが提供する実際のAPIと具体的なユースケースを見ながら学びます。",
            "また、その裏側でKotlinの言語機能（Suspend, Context Parameters）がどのように活かされているのかを紐解きます。",
            "Kotlinならではの強力な表現力を一緒に体感しましょう！",
        ),
    ),
    "session-21" to ProposalDetails(
        account = "@inetandev",
        category = "AI",
        level = "中級者",
        description = listOf(
            "AIによるコード生成やABテストによって機能変更の頻度が上がる一方、固定的なUIテストの保守や手動確認がリリース速度のボトルネックになりつつあります。",
            "LLMだけで動作確認を完結しようとすると、一部の指示がコンテキストに埋もれ、処理や判定の一貫性を保ちにくいという課題があります。",
            "本セッションでは、JetBrains製のKotlin向けAIエージェントフレームワークKoogを使い、テストの計画、実行、判定、報告を型付きの戦略グラフとして構築する方法を解説します。",
            "さらに、複数のテストにおけるLLM処理をCoroutinesで並行化し、共有する実機へのアクセスだけをSemaphoreで制御します。",
            "Androidアプリの動作確認を題材に、LLMへ任せる判断とKotlinコードで制御する処理を分離し、非決定的なエージェントの処理をレビュー・テスト可能にする設計を紹介します。",
        ),
    ),
    "session-01" to ProposalDetails(
        account = "@hiroaki08181359",
        category = "AI",
        level = "中級者",
        description = listOf(
            "新しいAndroidのAppFunctionsにより、AIエージェントが他のアプリと連携できるようになります。しかしその内部の詳細はドキュメントだけでは把握できません。",
            "本セッションでは内部実装を紐解きます。AppFunctionsにはエージェント側とツール側のしくみがあり、通常の開発で意識されるツール側だけではなく、エージェント側の実装も取り上げながら、しくみを解説します。",
            "AppFunctionsはKotlinで実装されています。KotlinでどのようにAppFunctionsが実現されているのか見ていきます。",
            "・AppFunctions概要",
            "・KDocからLLMへの関数説明をkspが生成する、AppSearchやプロセス間通信を介したツール検出/呼出しのしくみ等",
            "・Kotlin製エージェントフレームワークKoogを利用したエージェント例、ビルド時には未知のツールを動的に呼出す方法",
        ),
    ),
)

private fun targetAudience(level: String) = when (level) {
    "初心者" -> "Kotlinを始めたばかりの方"
    "中級者" -> "Kotlinを業務・個人開発で使用し、より深く理解したい方（脱初級者を目指す方）"
    "上級者" -> "Kotlinのコアな部分が分かる方、より深く知りたい方"
    else -> level
}

private fun venueName(venue: String) = when (venue) {
    "会場1" -> "fun - ホールA"
    "会場2" -> "val - ホールB"
    "会場3" -> "?.let - 402N"
    else -> venue
}

private val invitedTalk = UserSession(
    id = "invited-talk",
    time = "11:30 ～ 12:00",
    title = "Tales from Kotlin",
    speaker = "ジュラノフ　ヤン",
    affiliation = "JetBrains",
    account = "@yanewm",
    avatarPath = "/img/speakers/invited-talk.jpg",
    venue = "fun - ホールA / val - ホールB",
    category = "招待講演",
    level = null,
    description = listOf(
        "Kotlinの新機能は、ある日突然できあがるわけではありません。",
        "「こんなことができたらいい」というアイデアや、解決したい課題から始まり、さまざまな検討を経て、少しずつ形になっていきます。",
        "このセッションでは、現在Kotlinチームが取り組んでいる言語機能やツール改善からいくつかを取り上げ、その背景や目的、設計の考え方、そして今どこまで来ているのかを、ひとつひとつの物語として紹介します。Kotlinの少し先の未来を、一緒にのぞいてみましょう。",
    ),
    targetAudience = null,
)

object UserSessions {
    val all = listOf(
        invitedTalk,
        session("session-01", "13:20 ～ 14:00", "AppFunctionsの内部実装を紐解く", "hiro(Nosho Hiroaki)", null, "会場1"),
        session("session-02", "13:20 ～ 14:00", "AIコーディング時代のKotest：ユニットテストからAPIレベルの仕様固定化テストへ", "ogawan", "株式会社ヘンリー", "会場2"),
        session("session-03", "13:20 ～ 14:00", "Context parameters で作る！ はじめての型クラス", "munetoshi", "株式会社ナレッジワーク", "会場3"),
        session("session-04", "14:15 ～ 14:35", "HTTP QUERY時代の型安全な検索APIをKotlinで設計する", "近藤 成人", "Sansan株式会社", "会場1"),
        session(
            "session-05",
            "14:15 ～ 14:35",
            "ローカルデータベースをRoomで統一 — 大規模アルバイト求人アプリのKotlin Multiplatform実践",
            "Miyagawa Masataka",
            "ディップ株式会社",
            "会場2",
        ),
        session("session-06", "14:15 ～ 14:35", "eBPF Tracing for Kotlin", "HayasakaTakeru", "さくらインターネット株式会社", "会場3"),
        session("session-07", "14:50 ～ 15:30", "FIR Checkerで始めるAI時代のガードレール設計", "kitakkun", "株式会社ミラティブ", "会場1"),
        session("session-08", "14:50 ～ 15:30", "例外からRich Errorsまで、Kotlinエラーハンドリングの進化を読み解く", "shiita0903", "LINEヤフー株式会社", "会場2"),
        session("session-09", "14:50 ～ 15:30", "KotlinとUnityを安全・高速につなぐ ― C#ブリッジ自動生成と共有メモリ転送", "清水　亮介", "AnotherBall Pte. Ltd.", "会場3"),
        session("session-10", "15:55 ～ 16:15", "なぜ私たちは「現時点では」KMPの導入を見送ったのか", "chigichan24", "株式会社MIXI", "会場1"),
        session("session-11", "15:55 ～ 16:15", "祈るテストから、探索するテストへ - Lincheckから学ぶ並行処理テスト", "Honda Yusuke（yyh-gl）", "LINE Digital Frontier株式会社", "会場2"),
        session(
            "session-12",
            "15:55 ～ 16:15",
            "上位AIモデルが従量課金になった日 — 下位モデルにKotlinを書かせ切る、学生1人卒研のSpec駆動開発",
            "Satoru Takahashi",
            "公立はこだて未来大学・FUN ROBO STUDIO 株式会社",
            "会場3",
        ),
        session("session-13", "16:30 ～ 17:10", "共通基盤の実装に Kotlin / Quarkus を選定した理由と採用したアーキテクチャ", "野呂有我", "株式会社ニーリー", "会場1"),
        session(
            "session-14",
            "16:30 ～ 17:10",
            "Kotlin コンパイラに Contribute してみよう！ - Kotlin/Wasm で深い再帰を書けるようにした話",
            "Ayako Hayasaka",
            null,
            "会場2",
        ),
        session(
            "session-15",
            "16:30 ～ 17:10",
            "Kotlin × グラフ理論：Unstable APIで挑む関数レベルAffected Test Selectionライブラリ",
            "Kato Haruto",
            "株式会社ビズリーチ",
            "会場3",
        ),
        session("session-16", "17:25 ～ 17:45", "将来の標準フォーマッター？ktfmtを深掘る", "Task", "株式会社マネーフォワード", "会場1"),
        session("session-17", "17:25 ～ 17:45", "そのKDoc、Javadocのままになっていませんか？ 〜Kotlinらしい書き方とAI時代の新たな役割〜", "Akira Ito", null, "会場2"),
        session("session-18", "17:25 ～ 17:45", "切断・再接続・認証…BLEの修羅場をKotlin TCAで乗り切る", "車田　巡", "マツダ株式会社", "会場3"),
        session("session-19", "18:00 ～ 18:40", "Kotlin JVM用に作られたライブラリのKMPを前提とした再設計", "せきね じゅん", "株式会社コドモン", "会場1"),
        session("session-20", "18:00 ～ 18:40", "Arrowで学ぶ、関数型っぽくない関数型プログラミング", "長澤 太郎", null, "会場2"),
        session("session-21", "18:00 ～ 18:40", "LLM任せにしないUIテストワークフローをKoogの型付き戦略グラフで設計する", "andousan", "pixiv", "会場3"),
    )

    fun find(id: String?): UserSession? = all.find { it.id == id }

    private fun session(
        id: String,
        time: String,
        title: String,
        speaker: String,
        affiliation: String?,
        venue: String,
    ): UserSession {
        val proposal = proposalDetailsBySessionId.getValue(id)
        return UserSession(
            id = id,
            time = time,
            title = title,
            speaker = speaker,
            affiliation = affiliation,
            account = proposal.account,
            avatarPath = "/img/speakers/$id.${if (id == "session-16") "png" else "jpg"}",
            venue = venueName(venue),
            category = proposal.category,
            level = proposal.level,
            description = proposal.description,
            targetAudience = targetAudience(proposal.level),
        )
    }
}
